import { FC } from 'react';

import { Box, Flex, HStack, Image, Link, Text, VStack } from '@bonusly/design-system';

import { isIOSDevice } from '../../../../../utils/browser';
import { GenericAttachment } from '../../types/generic-attachment';

type LinkPreviewMetadataProps = {
	title?: string;
	description?: string;
	siteName?: string;
	descriptionLines: number;
};

const LinkPreviewMetadata: FC<LinkPreviewMetadataProps> = ({ title, description, siteName, descriptionLines }) => (
	<>
		{title && (
			<Text fontWeight="semibold" fontSize="sm" color="text.default" noOfLines={2} data-testid="link-preview-title">
				{title}
			</Text>
		)}
		{description && (
			<Text fontSize="xs" color="text.soft" noOfLines={descriptionLines} data-testid="link-preview-description">
				{description}
			</Text>
		)}
		{siteName && (
			<Text fontSize="xs" color="text.softer" data-testid="link-preview-site-name">
				{siteName}
			</Text>
		)}
	</>
);

type LinkPreviewAttachmentProps = {
	attachment: GenericAttachment;
};

export const LinkPreviewAttachment: FC<LinkPreviewAttachmentProps> = ({ attachment }) => {
	if (!attachment.url) {
		return null;
	}

	// iOS webviews behave poorly with target="_blank" - causes tab jump and back navigation
	const linkTarget = isIOSDevice() ? undefined : '_blank';

	const hasImage = !!attachment.image_url;

	return (
		<Link href={attachment.url} target={linkTarget} rel="noopener noreferrer" data-testid="link-preview-link" _hover={{ textDecoration: 'none' }}>
			<Box
				bg="bg.surface"
				borderRadius="lg"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				data-testid="link-preview-frame"
				transition="border-color 0.2s"
				_hover={{ borderColor: 'border.soft' }}
			>
				{hasImage ? (
					<Flex direction={{ base: 'column', sm: 'row' }}>
						<Box
							flexShrink={0}
							w={{ base: 'full', sm: '12.5rem' }}
							maxH={{ base: '10rem', sm: '7.5rem' }}
							overflow="hidden"
							display="flex"
							alignItems="center"
							justifyContent="center"
							bg="bg.subtle"
						>
							<Image
								src={attachment.image_url}
								alt=""
								objectFit="contain"
								maxW="full"
								maxH={{ base: '10rem', sm: '7.5rem' }}
								data-testid="link-preview-image"
							/>
						</Box>
						<VStack align="start" justify="center" p={3} spacing={1} flex={1} minW={0}>
							<LinkPreviewMetadata title={attachment.title} description={attachment.description} siteName={attachment.site_name} descriptionLines={2} />
						</VStack>
					</Flex>
				) : (
					<HStack p={3} spacing={3}>
						<VStack align="start" spacing={1} flex={1} minW={0}>
							<LinkPreviewMetadata title={attachment.title} description={attachment.description} siteName={attachment.site_name} descriptionLines={3} />
						</VStack>
					</HStack>
				)}
			</Box>
		</Link>
	);
};
